/**
 * SrtSegment vive acá desde Fase 18 (fue removido de types.ts).
 * ViewerScreen lo importa desde aquí.
 */
export type SrtSegment = {
  index: number;
  startTime: number; // segundos
  endTime: number;
  lines: string[];
};

// "00:01:23,456" → 83.456
function parseTimestamp(raw: string): number {
  const match = raw.match(/^(\d{2}):(\d{2}):(\d{2}),(\d{3})$/);
  if (!match) {
    throw new Error(`Invalid SRT timestamp: "${raw}"`);
  }
  const [, h, m, s, ms] = match;
  return (
    Number(h) * 3600 + Number(m) * 60 + Number(s) + Number(ms) / 1000
  );
}

// 83.456 → "00:01:23,456"
export function formatTimestamp(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    throw new Error(`Cannot format timestamp: ${seconds}`);
  }
  const totalMs = Math.round(seconds * 1000);
  const h = Math.floor(totalMs / 3_600_000);
  const m = Math.floor((totalMs % 3_600_000) / 60_000);
  const s = Math.floor((totalMs % 60_000) / 1000);
  const ms = totalMs % 1000;
  const pad2 = (n: number) => String(n).padStart(2, "0");
  const pad3 = (n: number) => String(n).padStart(3, "0");
  return `${pad2(h)}:${pad2(m)}:${pad2(s)},${pad3(ms)}`;
}

export function parseSrt(raw: string): SrtSegment[] {
  // BOM + normalizar line endings a \n
  const text = raw
    .replace(/^\uFEFF/, "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");

  // Bloques separados por una o más líneas en blanco
  const blocks = text.split(/\n\s*\n/).map((b) => b.replace(/^\n+|\n+$/g, ""));

  const segments: SrtSegment[] = [];

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (block.trim().length === 0) continue;

    const lines = block.split("\n");
    if (lines.length < 3) {
      throw new Error(
        `Block #${i + 1}: expected ≥3 lines (index, timestamps, text). Got ${lines.length}: ${JSON.stringify(block)}`,
      );
    }

    const idxStr = lines[0].trim();
    const idx = Number(idxStr);
    if (!Number.isInteger(idx)) {
      throw new Error(`Block #${i + 1}: invalid index "${idxStr}"`);
    }

    const tsMatch = lines[1].match(
      /^(\d{2}:\d{2}:\d{2},\d{3})\s+-->\s+(\d{2}:\d{2}:\d{2},\d{3})/,
    );
    if (!tsMatch) {
      throw new Error(
        `Block #${i + 1}: invalid timestamps line "${lines[1]}"`,
      );
    }

    const startTime = parseTimestamp(tsMatch[1]);
    const endTime = parseTimestamp(tsMatch[2]);
    if (endTime < startTime) {
      throw new Error(
        `Block #${i + 1}: endTime (${endTime}) < startTime (${startTime})`,
      );
    }

    const textLines = lines.slice(2);
    while (textLines.length > 0 && textLines.at(-1)!.trim() === "") {
      textLines.pop();
    }

    segments.push({ index: idx, startTime, endTime, lines: textLines });
  }

  return segments;
}

export function serializeSrt(segments: SrtSegment[]): string {
  const blocks = segments.map(
    (seg) =>
      `${seg.index}\n` +
      `${formatTimestamp(seg.startTime)} --> ${formatTimestamp(seg.endTime)}\n` +
      seg.lines.join("\n"),
  );
  // Bloque vacío final — convención de la mayoría de players
  return blocks.join("\n\n") + "\n";
}
